/**
 * Sticky header with show/hide on scroll
 * Automatically hides header when scrolling down and shows when scrolling up or near top
 */

export class StickyHeader extends HTMLElement {
  static UI_THRESHOLD = 200
  static SCROLL_THRESHOLD = window.innerHeight / 2
  static UI_VISIBLE_SELECTOR = '[data-ui~="visible"]'
  static UI_SCROLLED_SELECTOR = '[data-scrolled~="true"]'
  static MENU_ITEM_SELECTOR = '[data-header-menu-item]'
  lastScrollY: number
  ticking: boolean
  isVisible: boolean
  isScrolled: boolean
  elementHeight: number = 0
  headerElement: HTMLElement | null = null
  resizeObserver: ResizeObserver | null = null
  isUpdatingHeight: boolean = false
  resizeTimeout: number | null = null
  isChangingVisibility: boolean = false
  scrollYAtVisibilityChange: number = 0
  isHovering: boolean = false
  boundMenuItemEnter: ((e: MouseEvent) => void) | null = null
  visibilityAnimationFrameId: number | null = null
  static VISIBILITY_ANIMATION_DURATION_MS = 300

  constructor() {
    super()

    this.lastScrollY = window.scrollY
    this.ticking = false
    this.isVisible = true
    this.isScrolled = false
    this.resizeObserver = null
    this.isUpdatingHeight = false
    this.resizeTimeout = null
    this.isChangingVisibility = false
    this.scrollYAtVisibilityChange = 0
    this.isHovering = false

    this.handleScroll = this.handleScroll.bind(this)
    this.updateHeight = this.updateHeight.bind(this)
    this.handleMouseEnter = this.handleMouseEnter.bind(this)
    this.handleMouseLeave = this.handleMouseLeave.bind(this)
    this.boundMenuItemEnter = this.handleMenuItemEnter.bind(this)
  }

  connectedCallback() {
    this.syncVisibilityAttribute()

    requestAnimationFrame(() => {
      this.headerElement = this.querySelector('#header') as HTMLElement | null
      this.updateHeight()

      if (this.headerElement) {
        this.resizeObserver = new ResizeObserver(() => {
          if (!this.isUpdatingHeight && this.resizeTimeout === null) {
            if (this.resizeTimeout !== null) {
              clearTimeout(this.resizeTimeout)
            }
            this.resizeTimeout = window.setTimeout(() => {
              this.updateHeight()
              this.resizeTimeout = null
            }, 100)
          }
        })
        this.resizeObserver.observe(this.headerElement)
      }

      window.addEventListener('scroll', this.handleScroll, { passive: true })
      window.addEventListener('resize', this.updateHeight, { passive: true })
      this.addEventListener('mouseenter', this.handleMouseEnter)
      this.addEventListener('mouseleave', this.handleMouseLeave)
      this.addEventListener('mouseover', this.boundMenuItemEnter!)

      this.handleScroll()
    })
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.updateHeight)
    this.removeEventListener('mouseenter', this.handleMouseEnter)
    this.removeEventListener('mouseleave', this.handleMouseLeave)
    if (this.boundMenuItemEnter) {
      this.removeEventListener('mouseover', this.boundMenuItemEnter)
    }
    if (this.visibilityAnimationFrameId !== null) {
      cancelAnimationFrame(this.visibilityAnimationFrameId)
      this.visibilityAnimationFrameId = null
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  }

  updateHeight() {
    if (this.isUpdatingHeight) return

    this.isUpdatingHeight = true
    this.updateHeaderHeightVariable()
    this.isUpdatingHeight = false
  }

  checkForVisibleUI() {
    return this.querySelector(StickyHeader.UI_VISIBLE_SELECTOR)
  }

  updateHeaderHeightVariable() {
    this.headerElement = this.querySelector('#header') as HTMLElement | null
    if (this.headerElement) {
      this.elementHeight = this.headerElement.getBoundingClientRect().height
    } else {
      this.elementHeight = this.getBoundingClientRect().height
    }
    document.documentElement.style.setProperty('--height-header-adapt', `${this.elementHeight}px`)
  }

  /**
   * Updates --height-header-adapt every frame during the show/hide animation
   * so elements relying on it can resize/reposition smoothly.
   */
  runHeightSyncDuringAnimation(durationMs: number, onComplete: () => void) {
    if (this.visibilityAnimationFrameId !== null) {
      cancelAnimationFrame(this.visibilityAnimationFrameId)
      this.visibilityAnimationFrameId = null
    }
    const start = performance.now()
    const tick = () => {
      this.updateHeaderHeightVariable()
      const elapsed = performance.now() - start
      if (elapsed < durationMs) {
        this.visibilityAnimationFrameId = requestAnimationFrame(tick)
      } else {
        this.visibilityAnimationFrameId = null
        onComplete()
      }
    }
    this.visibilityAnimationFrameId = requestAnimationFrame(tick)
  }

  syncVisibilityAttribute() {
    if (this.isVisible) {
      this.setAttribute('data-inview', 'inview')
    } else {
      this.removeAttribute('data-inview')
    }
  }

  showHeader() {
    if (!this.isVisible && !this.isChangingVisibility) {
      this.isChangingVisibility = true
      this.scrollYAtVisibilityChange = window.scrollY

      if (this.resizeObserver && this.headerElement) {
        this.resizeObserver.unobserve(this.headerElement)
      }

      this.isVisible = true
      this.syncVisibilityAttribute()

      if (this.resizeTimeout !== null) {
        clearTimeout(this.resizeTimeout)
        this.resizeTimeout = null
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.runHeightSyncDuringAnimation(StickyHeader.VISIBILITY_ANIMATION_DURATION_MS, () => {
            if (this.resizeObserver && this.headerElement) {
              this.resizeObserver.observe(this.headerElement)
            }
            this.isChangingVisibility = false
          })
        })
      })
    }
  }

  hideHeader() {
    if (this.isVisible && !this.isChangingVisibility && !this.isHovering) {
      this.isChangingVisibility = true
      this.scrollYAtVisibilityChange = window.scrollY

      if (this.resizeObserver && this.headerElement) {
        this.resizeObserver.unobserve(this.headerElement)
      }

      this.isVisible = false
      this.syncVisibilityAttribute()

      if (this.resizeTimeout !== null) {
        clearTimeout(this.resizeTimeout)
        this.resizeTimeout = null
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.runHeightSyncDuringAnimation(StickyHeader.VISIBILITY_ANIMATION_DURATION_MS, () => {
            if (this.resizeObserver && this.headerElement) {
              this.resizeObserver.observe(this.headerElement)
            }
            this.isChangingVisibility = false
          })
        })
      })
    }
  }

  registerScroll() {
    this.isScrolled = true
    this.setAttribute('data-scrolled', 'true')
  }

  unregisterScroll() {
    this.isScrolled = false
    this.removeAttribute('data-scrolled')
  }

  handleMouseEnter() {
    this.isHovering = true
    if (!this.isVisible) {
      this.showHeader()
    }
  }

  handleMouseLeave() {
    this.isHovering = false
    this.clearOpenSubmenu()
  }

  handleMenuItemEnter(e: MouseEvent) {
    const item = (e.target as HTMLElement).closest<HTMLElement>(StickyHeader.MENU_ITEM_SELECTOR)
    if (item) {
      this.setOpenSubmenu(item)
    }
  }

  setOpenSubmenu(menuItem: HTMLElement) {
    if (menuItem.getAttribute('data-submenu-open') === 'true') return
    this.querySelectorAll(StickyHeader.MENU_ITEM_SELECTOR).forEach((el) => {
      el.removeAttribute('data-submenu-open')
    })
    menuItem.setAttribute('data-submenu-open', 'true')
  }

  clearOpenSubmenu() {
    this.querySelectorAll(StickyHeader.MENU_ITEM_SELECTOR).forEach((el) => {
      el.removeAttribute('data-submenu-open')
    })
  }

  handleScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        const currentScroll = window.scrollY

        if (this.isChangingVisibility) {
          this.lastScrollY = currentScroll
          this.ticking = false
          return
        }

        const isScrollingDown = currentScroll > this.lastScrollY
        const isScrollingUp = currentScroll < this.lastScrollY
        const scrollChangedSinceVisibilityChange = Math.abs(currentScroll - this.scrollYAtVisibilityChange) > 10

        if (currentScroll < StickyHeader.SCROLL_THRESHOLD) {
          this.unregisterScroll()
        } else {
          this.registerScroll()
        }

        if (currentScroll < StickyHeader.UI_THRESHOLD) {
          if (!this.isVisible) {
            this.showHeader()
          }
        } else {
          if (isScrollingUp && scrollChangedSinceVisibilityChange) {
            if (!this.isVisible) {
              this.showHeader()
            }
          } else if (isScrollingDown && scrollChangedSinceVisibilityChange && !this.isHovering) {
            if (this.isVisible) {
              this.hideHeader()
            }
          }
        }

        this.lastScrollY = currentScroll
        this.ticking = false
      })

      this.ticking = true
    }
  }
}
